/** Floating Vertex360 Copilot assistant — layout & persistence keys */

export const VERTEX360_COPILOT_FLOATING_SIZE_PX = 55;
export const VERTEX360_COPILOT_FLOATING_LOTTIE_SIZE_PX = 45;
export const VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING = 16;

/** Default placement: top-right — distance from right & top edges (px). */
export const VERTEX360_COPILOT_FLOATING_DEFAULT_ANCHOR = { anchorRight: 23, anchorTop: 87 };

/** Routes where the FAB default overlaps table action columns — snap to bottom-right. */
export const VERTEX360_COPILOT_FORMS_ADMIN_ROUTES = [
  "/forms-list",
  "/forms-list-hrm",
  "/forms-accommodation",
];

/** Below drawer (100000), above app shell / livechat */
export const VERTEX360_COPILOT_FLOATING_Z_INDEX = 99990;

export const VERTEX360_COPILOT_FLOATING_ANCHOR_KEY = "vertex360Copilot.floatingAnchor.v1";

/** @deprecated Legacy pixel storage — migrated to anchor on read */
const VERTEX360_COPILOT_FLOATING_POSITION_KEY_LEGACY = "vertex360Copilot.floatingPosition.v2";

/** Dismiss intro until next browser refresh */
export const VERTEX360_COPILOT_FLOATING_INTRO_DISMISSED_SESSION_KEY =
  "vertex360Copilot.introDismissed";

/** Movement below this (px) counts as a click, not a drag */
export const VERTEX360_COPILOT_FLOATING_DRAG_THRESHOLD_PX = 6;

export const VERTEX360_COPILOT_FLOATING_TOOLTIP = {
  title: "Vertex360 Copilot",
  description:
    "Ask Vertex360 Copilot about organisation data, NDIS guidance, and how to use the platform. Drag to move, click to open.",
};

export const VERTEX360_COPILOT_FLOATING_INTRO = {
  title: "Meet Vertex360 Copilot",
  description:
    "Vertex360 Copilot helps you answer questions, find organisation records, and navigate Vertex360 more efficiently.",
  dismissLabel: "Got it",
};

/**
 * @typedef {{ anchorRight: number, anchorTop: number }} CopilotFloatingAnchor
 */

/**
 * @param {number} x
 * @param {number} y
 * @returns {CopilotFloatingAnchor}
 */
export const pixelsToCopilotFloatingAnchor = (x, y) => {
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const size = VERTEX360_COPILOT_FLOATING_SIZE_PX;
  const pad = VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING;

  return {
    anchorRight: Math.max(pad, vw - x - size),
    anchorTop: Math.max(pad, y),
  };
};

/**
 * @param {CopilotFloatingAnchor} anchor
 * @returns {{ x: number, y: number }}
 */
export const anchorToCopilotFloatingPixels = (anchor) => {
  const size = VERTEX360_COPILOT_FLOATING_SIZE_PX;
  const pad = VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING;
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;

  const x = vw - size - anchor.anchorRight;
  const y = anchor.anchorTop;

  const maxX = Math.max(pad, vw - size - pad);
  const maxY = Math.max(pad, vh - size - pad);

  return {
    x: Math.min(maxX, Math.max(pad, x)),
    y: Math.min(maxY, Math.max(pad, y)),
  };
};

/**
 * @param {{ x: number, y: number }} position
 * @returns {{ x: number, y: number }}
 */
export const clampCopilotFloatingPosition = (position) => {
  const size = VERTEX360_COPILOT_FLOATING_SIZE_PX;
  const pad = VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING;
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  const maxX = Math.max(pad, vw - size - pad);
  const maxY = Math.max(pad, vh - size - pad);

  return {
    x: Math.min(maxX, Math.max(pad, position.x)),
    y: Math.min(maxY, Math.max(pad, position.y)),
  };
};

/**
 * @param {CopilotFloatingAnchor} anchor
 * @returns {CopilotFloatingAnchor}
 */
export const clampCopilotFloatingAnchor = (anchor) => {
  const size = VERTEX360_COPILOT_FLOATING_SIZE_PX;
  const pad = VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING;
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  const maxAnchorRight = Math.max(pad, vw - size - pad);
  const maxAnchorTop = Math.max(pad, vh - size - pad);

  return {
    anchorRight: Math.min(maxAnchorRight, Math.max(pad, anchor.anchorRight)),
    anchorTop: Math.min(maxAnchorTop, Math.max(pad, anchor.anchorTop)),
  };
};

/** @returns {CopilotFloatingAnchor} */
export const getCopilotFloatingFormsPageAnchor = () => {
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const size = VERTEX360_COPILOT_FLOATING_SIZE_PX;
  const pad = VERTEX360_COPILOT_FLOATING_VIEWPORT_PADDING;

  return clampCopilotFloatingAnchor({
    anchorRight: VERTEX360_COPILOT_FLOATING_DEFAULT_ANCHOR.anchorRight,
    anchorTop: Math.max(pad, vh - size - pad - 24),
  });
};

/**
 * @returns {CopilotFloatingAnchor}
 */
export const getDefaultCopilotFloatingAnchor = () => ({
  anchorRight: VERTEX360_COPILOT_FLOATING_DEFAULT_ANCHOR.anchorRight,
  anchorTop: VERTEX360_COPILOT_FLOATING_DEFAULT_ANCHOR.anchorTop,
});

const isValidAnchor = (value) =>
  typeof value?.anchorRight === "number" &&
  typeof value?.anchorTop === "number" &&
  Number.isFinite(value.anchorRight) &&
  Number.isFinite(value.anchorTop);

const migrateLegacyPixelsToAnchor = (x, y) => {
  const clamped = clampCopilotFloatingPosition({ x, y });
  return pixelsToCopilotFloatingAnchor(clamped.x, clamped.y);
};

/**
 * @returns {CopilotFloatingAnchor | null}
 */
export const readCopilotFloatingAnchor = () => {
  try {
    const raw = localStorage.getItem(VERTEX360_COPILOT_FLOATING_ANCHOR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidAnchor(parsed)) {
        return clampCopilotFloatingAnchor(parsed);
      }
    }

    const legacyRaw = localStorage.getItem(VERTEX360_COPILOT_FLOATING_POSITION_KEY_LEGACY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (
        typeof legacy?.x === "number" &&
        typeof legacy?.y === "number" &&
        Number.isFinite(legacy.x) &&
        Number.isFinite(legacy.y)
      ) {
        const migrated = migrateLegacyPixelsToAnchor(legacy.x, legacy.y);
        writeCopilotFloatingAnchor(migrated);
        localStorage.removeItem(VERTEX360_COPILOT_FLOATING_POSITION_KEY_LEGACY);
        return migrated;
      }
    }
  } catch {
    // ignore corrupt storage
  }
  return null;
};

/**
 * @param {CopilotFloatingAnchor} anchor
 */
export const writeCopilotFloatingAnchor = (anchor) => {
  try {
    const next = clampCopilotFloatingAnchor(anchor);
    localStorage.setItem(VERTEX360_COPILOT_FLOATING_ANCHOR_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
};

/**
 * Resolve anchor (saved, legacy-migrated, or default) to clamped pixel position.
 * @returns {{ x: number, y: number }}
 */
export const resolveCopilotFloatingPosition = () => {
  const anchor =
    readCopilotFloatingAnchor() || getDefaultCopilotFloatingAnchor();
  return anchorToCopilotFloatingPixels(anchor);
};

/** @deprecated Use resolveCopilotFloatingPosition */
export const getDefaultCopilotFloatingPosition = () =>
  anchorToCopilotFloatingPixels(getDefaultCopilotFloatingAnchor());

/** @deprecated Use readCopilotFloatingAnchor */
export const readCopilotFloatingPosition = () => {
  const anchor = readCopilotFloatingAnchor();
  return anchor ? anchorToCopilotFloatingPixels(anchor) : null;
};

/** @deprecated Use writeCopilotFloatingAnchor */
export const writeCopilotFloatingPosition = (position) => {
  writeCopilotFloatingAnchor(pixelsToCopilotFloatingAnchor(position.x, position.y));
};
